import type {
  attributes,
  elementNode,
  serializedNodeWithId,
  textNode,
} from '@carboncopyco/rrweb-snapshot';
import { NodeType } from '@carboncopyco/rrweb-snapshot';
import type {
  addedNodeMutation,
  eventWithTime,
} from '@carboncopyco/rrweb-types';
import type { Tour } from '@carboncopyco/types';
import jp from 'jsonpath';

// Inline
const ID_PREFIX = '465463';
const JP_LINK_TAG_PATH = `$..*[?(@.tagName==="link" && @.attributes.rel==="stylesheet")]`;

const getPathsToLinkContainingParents = (events: eventWithTime[]) => {
  const uniquePaths = new Set<string>();
  const allPaths = jp
    .paths(events, JP_LINK_TAG_PATH)
    .map((path) => {
      const sliceAt = path.at(-2) === 'childNodes' ? -1 : -2;
      return path.slice(0, sliceAt);
    })
    .map((path) => jp.stringify(path));

  for (const path of allPaths) {
    uniquePaths.add(path);
  }

  return Array.from(uniquePaths);
};

const getContentFromLinkTag = async (
  fetchUrl: string,
  fileServiceToken: Tour['fileServiceToken']
) => {
  let content = '';
  if (!fetchUrl) {
    return content;
  }

  const formattedToken = fileServiceToken ? `?token=${fileServiceToken}` : '';

  try {
    const response = await fetch(fetchUrl + formattedToken);
    if (response.ok) {
      content = await response.text();
    }
  } catch (_) {
    return content;
  }
  return content;
};

const generateTextNode = (
  node: serializedNodeWithId,
  textContent: string,
  id: serializedNodeWithId['id'],
  rootId: serializedNodeWithId['rootId']
) => {
  return {
    type: NodeType.Text,
    textContent,
    isStyle: true,
    rootId,
    id: ID_PREFIX + id,
    ...(node.isShadow || node.isShadowHost ? { isShadow: true } : {}),
  } as textNode;
};

const convertLinkToStyle = async (
  nodeOrMutation: serializedNodeWithId | addedNodeMutation,
  fileServiceToken: Tour['fileServiceToken'],
  isMutation: boolean
) => {
  const node = isMutation
    ? (nodeOrMutation as addedNodeMutation).node
    : (nodeOrMutation as serializedNodeWithId);

  const { id, rootId } = node;
  const { attributes } = node as elementNode;

  const fetchUrl = String(attributes?.href || '');

  const modifiedAttributes = Object.keys(attributes).reduce((prev, next) => {
    return {
      ...prev,
      ...(next === 'href'
        ? {
            [`data-ds-original-${next}`]: attributes[next],
          }
        : { [next]: attributes[next] }),
    };
  }, {} as attributes);

  const textContent = await getContentFromLinkTag(fetchUrl, fileServiceToken);
  const errorReturn = isMutation
    ? [nodeOrMutation as addedNodeMutation]
    : (nodeOrMutation as elementNode);

  if (!textContent) {
    return errorReturn;
  }

  try {
    if (isMutation) {
      const nodeMutation = nodeOrMutation as addedNodeMutation;
      return [
        {
          ...nodeMutation,
          node: {
            ...nodeMutation.node,
            tagName: 'style',
            attributes: modifiedAttributes,
          },
        },
        {
          parentId: id,
          nextId: null,
          node: generateTextNode(nodeMutation.node, textContent, id, rootId),
        },
      ] as unknown as addedNodeMutation[];
    }

    const nodeElement = nodeOrMutation as serializedNodeWithId;
    return {
      ...nodeElement,
      tagName: 'style',
      attributes: modifiedAttributes,
      childNodes: [generateTextNode(nodeElement, textContent, id, rootId)],
    } as unknown as elementNode;
  } catch (_error) {
    return errorReturn;
  }
};

const getModifiedParents = async (
  events: eventWithTime[],
  fileServiceToken: Tour['fileServiceToken']
) => {
  const pathsToLinkContainingParents = getPathsToLinkContainingParents(events);
  const modifiedParents: {
    path: string;
    value: any;
  }[] = [];

  for (const pathToLinkContainingParents of pathsToLinkContainingParents) {
    const parentValue = jp.value(events, pathToLinkContainingParents);
    modifiedParents.push({
      path: pathToLinkContainingParents,
      value: await getModifiedParent(parentValue, fileServiceToken),
    });
  }

  return modifiedParents;
};

const getModifiedParent = async (
  parent: (addedNodeMutation | elementNode)[],
  fileServiceToken: Tour['fileServiceToken']
) => {
  const checkIfLinkStylesheet = (node: elementNode) =>
    node.tagName === 'link' && node.attributes.rel === 'stylesheet';

  return await parent?.reduce(
    async (prev, next) => {
      const isMutation = !!(next as addedNodeMutation).node;

      if (isMutation) {
        const mutation = next as addedNodeMutation;
        if (
          mutation.node &&
          checkIfLinkStylesheet(mutation.node as elementNode)
        ) {
          const modifiedNodeMutation = await convertLinkToStyle(
            mutation,
            fileServiceToken,
            isMutation
          );
          return [
            ...(await prev),
            ...(modifiedNodeMutation as addedNodeMutation[]),
          ];
        }
      }

      if (!isMutation) {
        const serializedNode = next as serializedNodeWithId;
        if (
          serializedNode &&
          checkIfLinkStylesheet(serializedNode as elementNode)
        ) {
          const modifiedNode = await convertLinkToStyle(
            serializedNode,
            fileServiceToken,
            isMutation
          );
          return [...(await prev), modifiedNode];
        }
      }

      return [...(await prev), next];
    },
    Promise.resolve([] as (addedNodeMutation | elementNode)[])
  );
};

const applyModifications = async (
  events: eventWithTime[],
  fileServiceToken: Tour['fileServiceToken']
) => {
  const modifiedParents = await getModifiedParents(events, fileServiceToken);

  for await (const { path, value } of modifiedParents) {
    jp.apply(events, path, () => value);
  }

  return events;
};

export const getFlickerProcessedEvents = (
  events: eventWithTime[],
  fileServiceToken: Tour['fileServiceToken']
) => {
  return applyModifications(events, fileServiceToken);
};
